/**
 * DashboardLayoutV2 — Rebirth shell.
 *
 * Per Agentlets-Design-Principles.md §3.2 + Implementation-Plan.md §3.1:
 *   - Deep navy sidebar (the only dark element)
 *   - White topbar with kill-switch indicator + approvals badge + ⌘K + user
 *   - 5-section IA: Operate / Govern / Build / Administer / System
 *   - Composes all v2 primitives
 *
 * The shell renders v1 page components inside its main slot until each
 * page is migrated to v2 in its own phase. That's the §5.3 composition
 * rule — the shell can flip to v2 independently of any individual page.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { navItemVisible } from '../data/routeGates';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Bell, HelpCircle, ChevronDown, ChevronRight, Menu, X, LogOut,
  LayoutGrid, Bot, Workflow, CheckSquare, Compass,
  Shield, FileText, Sparkles as SparklesIcon,
  Cable, Store, FileCode, Settings as SettingsIcon, Users, UserCheck, Building2, KeyRound,
  Brain, Flag, Fingerprint, Boxes, Lock,
  // Cloud is used by the new SYSTEM section "Operators Console" (plural) nav
  // entry — the PA-only deployment-management surface added by the Operators
  // Console initiative. Distinct from the legacy ADMINISTER → "Operator
  // Console" (singular, runtime triage) which keeps its SettingsIcon.
  Cloud,
  // RC-227 — My Tasks unified inbox. Inbox is the right semantic (it's
  // the operator's queue of "things waiting for me" across Forms +
  // Attestations); CheckSquare is already in use by Approvals.
  Inbox,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useScopeSwitch from '../hooks/useScopeSwitch';
import { usePlatform } from '../contexts/PlatformContext';
import { useNavigation } from '../contexts/NavigationContext';
import * as api from '../services/api';
import { connectApprovalWebSocket, disconnectApprovalWebSocket } from '../services/websocket';
import { isNavSectionEnabled } from '../utils/featureFlags';
import { useEntitlements } from '../hooks/useEntitlements';
import { NAV_ITEM_MODULE } from '../utils/entitlementAccess';
import GlobalSearchInline from './v2/GlobalSearchInline';
// RC-224 — Global SSE toast: subscribe in the app shell so form_task
// notifications fire from any page, not just /forms/to-do. The notifier
// hook auto-suppresses when the user is already on the Forms surfaces
// (toast would be redundant noise there).
import GlobalToastStack from '../components/v2/GlobalToastStack';
import useFormTaskToastNotifier from '../hooks/useFormTaskToastNotifier';
// RC-227 — My Tasks left-nav badge live count. Aggregates Forms +
// Attestations pending count via the RC-224 SSE bus singleton (shared
// with the global form-task toast notifier above — refcounted, no
// duplicate EventSource). See useMyTasksPendingCount.js for the fetch
// + debounced refresh contract.
//
// 2026-07-04 — useFormsPendingCount previously imported here for a
// per-nav-item Forms badge is now unused; the /forms surface became
// author-only when /forms/to-do redirected to /my-tasks. The hook
// file remains alongside the SSE bus utilities for any future caller.
import useMyTasksPendingCount from '../hooks/useMyTasksPendingCount';

/* ────────────────────────────────────────────────────────────────
   NAV STRUCTURE — the new IA from Design Principles §3.2
   ──────────────────────────────────────────────────────────────── */

const NAV = [
  {
    label: 'Operate',
    flagKey: 'operations',
    items: [
      { id: 'overview',          label: 'Outcomes',     path: '/overview',     icon: LayoutGrid },
      { id: 'agents',            label: 'Agentlets',    path: '/agents',       icon: Bot, badgeKey: 'agents' },
      { id: 'canvas',            label: 'Workflows',    path: '/canvas',       icon: Workflow },
      /* Identity — Phase 6 (P0-IDENT). Top-level operational surface
       * for SPIFFE/SVID lifecycle, federation, and trust-domain config
       * across Org / Tenant / Agent scopes. Lives in Operate (alongside
       * Workflows and Approvals) because identity is something you RUN,
       * not a config screen — see ADR-Identity-Plane.md. */
      { id: 'identity',          label: 'Identity',     path: '/identity',     icon: Fingerprint },
      /* Intent Profiles — Catalog v1.0 Phase 1E.1. Placed directly below
       * Identity per the design directive. Compliance authors templates;
       * Developers bind agents to specific approved versions; runtime
       * (Phase 1D, behind feature flag) enforces. */
      { id: 'intent-profiles',   label: 'Intent Profiles', path: '/intent-profiles', icon: Boxes },
      { id: 'approvals',         label: 'Approvals',    path: '/approvals',    icon: CheckSquare, badgeKey: 'approvals' },
      /* RC-161 — Forms · To Do (Shape B form_task fill surface).
       * First-class primitive per operator directive 2026-06-22.
       * Lands directly below Approvals: separates DATA-COLLECTION
       * (Forms) from DECISIONS (Approvals). The /forms route
       * currently redirects to /forms/to-do; the Library tab
       * (RC-160) lands as a sibling shortly. */
      // RC-227 — My Tasks unified inbox (Forms + Attestations). This is
      // the PRIMARY assignee surface. Live badge fires over the SSE bus
      // singleton (RC-224).
      { id: 'my-tasks',          label: 'My Tasks',     path: '/my-tasks',     icon: Inbox, badgeKey: 'myTasks' },
      // 2026-07-04 — Forms nav item retired the `badgeKey: 'forms'` wiring
      // because /forms/to-do was retired in the same commit (it duplicated
      // /my-tasks). Forms nav is now the AUTHOR/LIBRARY surface only —
      // Library + Designer tabs, no "waiting on you" content, no badge.
      // Assignee counts live on the My Tasks nav item above.
      { id: 'forms',             label: 'Forms',        path: '/forms',        icon: FileText },
    ],
    subGroups: [
      {
        label: 'Discovery',
        flagKey: 'discoveryGel',
        icon: Compass,
        items: [
          /* Phase F — sidebar now points to the Discovery Hub (default tab=agents).
           * /shadow-discovery URL remains routable for back-compat bookmarks. The id
           * stays 'shadow-discovery' so the active-route highlight + permission
           * gates that key off the id continue to work.
           *
           * P7J-RUN-014 — labels were "Agents" and "Workflows" verbatim, which
           * collided with the same-named entries under Operate (governed) — two
           * "Agents" and two "Workflows" links in the sidebar with completely
           * different meanings. Renamed to "Shadow Agents" / "Shadow Workflows"
           * so the disambiguation is immediate without hovering. */
          { id: 'shadow-discovery',   label: 'Shadow Agents',     path: '/discovery?tab=agents' },
          { id: 'workflow-discovery', label: 'Shadow Workflows',  path: '/workflow-discovery' },
        ],
      },
    ],
  },
  {
    label: 'Protect',
    flagKey: 'governance',
    items: [
      { id: 'protection-hub',          label: 'Protection Center',     path: '/protection-hub',          icon: Shield },
      { id: 'accountability',          label: 'Accountability',        path: '/accountability',          icon: UserCheck },
      { id: 'policy-config',           label: 'Policies',              path: '/policy-config',           icon: FileText },
      { id: 'skills',                  label: 'Skill Center',          path: '/skills',                  icon: SparklesIcon },
    ],
    subGroups: [
      {
        label: 'Protection views',
        flagKey: 'governance',
        items: [
          { id: 'native-trust',           label: 'Trust',                  path: '/native-trust' },
          { id: 'compliance',             label: 'Compliance',             path: '/compliance' },
          { id: 'workflow-audit',         label: 'Audit Trail',            path: '/workflow-audit' },
          { id: 'workflow-intelligence',  label: 'Workflow Intelligence',  path: '/workflow-intelligence' },
        ],
      },
      {
        /* V1-parity (DEF-V2-NAV-EXTELETS-001) — V1's DashboardLayout
         * exposed an "ExteLets" sub-group with Registrations + Platform
         * Connectors entries (see DashboardLayout.jsx:84-96). V2 had
         * dropped this subGroup entirely when the sidebar was redesigned,
         * orphaning the /gel-agents and /gel-connectors routes — the
         * pages still rendered when the URL was typed directly, but
         * there was no breadcrumb / sidebar link to reach them from the
         * Discovery promote flow OR the operator's normal navigation.
         *
         * Restored here, scoped to Govern (matching V1's placement),
         * gated by the same externalAgents feature flag V1 used. The
         * Visibility of 'gel-agents'/'gel-connectors' comes from their own
         * route gates (data/routeGates.js), so the role gate is unchanged. */
        label: 'ExteLets',
        flagKey: 'externalAgents',
        items: [
          /* R3.1 — Registrations now opens the canonical /agents inventory
           * pre-filtered to ExteLet. The legacy /gel-agents page remains
           * routable (typed URL, Discovery promote deep links) until R3.3
           * lands the rich detail tabs in AgentDetailPageV2 and we can
           * retire it entirely. AgentsInventoryPageV2 reads ?type= on
           * mount and pre-selects the archetype facet. */
          { id: 'gel-agents',      label: 'Registrations',       path: '/agents?type=ExteLet' },
          /* Renamed 2026-05-26 — "Platform Connectors" was confused with
           * the BUILD section's "Connectors" entry. The ExteLet construct
           * is specifically a binding to an external AI agent framework
           * (Vertex AI, Anthropic, CrewAI, LangChain, custom MCP, etc.),
           * which is conceptually different from data-source connectors.
           * Label aligned with the construct's actual purpose. */
          { id: 'gel-connectors',  label: 'Agent Framework', path: '/gel-connectors' },
        ],
      },
    ],
  },
  {
    label: 'Build',
    flagKey: 'manage',
    items: [
      { id: 'connectors',         label: 'Connectors',        path: '/connectors',        icon: Cable },
      { id: 'agent-marketplace',  label: 'Agent Marketplace', path: '/agent-marketplace', icon: Store },
      { id: 'agentic-app-packs',  label: 'Agentic App Packs', path: '/agentic-app-packs', icon: Boxes },
      { id: 'templates',          label: 'Templates',         path: '/templates',         icon: FileCode },
    ],
  },
  {
    label: 'Administer',
    flagKey: 'settings',
    items: [
      { id: 'admin-center',   label: 'Admin Center',   path: '/admin-center',   icon: SettingsIcon, dotKey: 'killSwitch' },
      { id: 'team',           label: 'Team',           path: '/team',           icon: Users },
      // Secrets Manager (Phase 1) — store credentials in the configured vault
      // (GCP / HashiCorp / AWS / Azure / Local), get back vault://... references
      // to paste into Connectors / Agents / Skills. Backed by /api/v1/secrets
      // (existing controller; new GET /secrets endpoint added in Phase 1).
      { id: 'secrets',        label: 'Secrets Manager',path: '/secrets',        icon: Lock },
      // V191 / Phase-5 — Deployments page is now Multi-Tenancy. The /deployments
      // route bounces to /multi-tenancy via DeploymentsRouteResolver (legacy=1
      // still loads the V1 page during the migration window).
      { id: 'multi-tenancy',  label: 'Multi-Tenancy',  path: '/multi-tenancy',  icon: Building2 },
      // V208 / Phase E-1 — Customer picker page. Platform Admin only.
      // Backed by GET /api/v1/customers + POST /api/v1/auth/switch-customer.
      // Its route gate (minRole Platform Admin) is what hides it from everyone
      // else; the rail reads that same gate rather than a second allowlist.
      { id: 'customers',      label: 'Customers',      path: '/customers',      icon: Building2 },
      // V208 / Phase E-4 — Operator Console. Operator role only.
      // Runtime triage surface (kill switch, execution feed).
      { id: 'operator-console', label: 'Operator Console', path: '/operator-console', icon: SettingsIcon },
      /* Cloud Operations — Platform Admin + Super Admin surface for managing
       * Prove7 cluster deployments across Prove7 Cloud and Self-Hosted modes.
       * PA sees cross-customer; Super Admin sees their customer-scoped subset.
       *
       * DISAMBIGUATION: distinct from "Operator Console" above — that's the
       * SRE runtime-triage surface (kill switch, agent runtime). Cloud
       * Operations is about deployment management (clusters, cloud accounts,
       * targets, deployment workflows, SLA matrix).
       *
       * Companion: Prove7_Operators_Console_Design.docx (the design doc kept
       * its original filename; the surface itself was renamed during W2.x to
       * avoid the near-collision with "Operator Console"). */
      { id: 'cloud-operations', label: 'Cloud Operations', path: '/cloud-operations', icon: Cloud },
    ],
    subGroups: [
      {
        label: 'Governed Gateway',
        flagKey: 'governedGateway',
        icon: KeyRound,
        items: [
          { id: 'gateway-keys',      label: 'API Keys',  path: '/gateway-keys' },
          { id: 'gateway-dashboard', label: 'Dashboard', path: '/gateway-dashboard' },
        ],
      },
    ],
  },
  {
    /* RC-89 — SYSTEM section is Platform Admin EXCLUSIVE.
     *
     * Pre-RC-89 the section was labeled "System · Super Admin" and
     * gated on `superAdminOnly: true`, which inverted the RBAC
     * hierarchy:
     *   - Super Admin (customer-scoped) SAW Intelligence Center +
     *     Feature Flags — both of which mutate platform-wide state
     *     across every customer. That's a real authority leak: SA
     *     could toggle flags or read platform intelligence outside
     *     the customer they administer.
     *   - Platform Admin (global authority) DID NOT see the section
     *     at all, so the only role that legitimately owns these
     *     pages was locked out of the nav entry.
     *
     * Per RBAC_RULE_MAP.md §1.5 + the canonical wildcard taxonomy
     * (reference_rbac_wildcard_scope memory + the no-pa-sa-clubbing
     * standing rule): Platform Admin is the ONLY platform-wide `*`.
     * Super Admin's `*` is customer-bounded. Any nav surface that
     * mutates platform-wide state — role matrix, feature flags,
     * intelligence center — must gate on `isPlatformAdmin`, not
     * `isSuperAdmin`. Fixed here + at the filter site below. The per-id half
     * of that fix now lives in each route's own gate: 'intelligence' is
     * minRole Super Admin and 'flags' is exactRole Platform Admin, so neither
     * can be reached by a customer-bounded Super Admin. */
    label: 'System · Platform Admin',
    flagKey: 'intelligence',
    platformAdminOnly: true,
    items: [
      /* NOTE — "Operators Console" was renamed to "Cloud Operations" and
       * moved to the ADMINISTER section above (also opened up to Super Admin
       * customer-scoped). Intentionally not duplicated here. */
      { id: 'intelligence', label: 'Intelligence Center', path: '/intelligence', icon: Brain },
      { id: 'flags',        label: 'Feature Flags',       path: '/flags',        icon: Flag },
    ],
  },
];

/* Same role-id allow list as v1 — keeps RoleGuard semantics consistent.
 *
 * V191 / Phase-5 — replaced 'deployments' nav id with 'multi-tenancy'
 * (Administer → Multi-Tenancy supersedes Administer → Deployments).
 * Tenant Admin gains visibility because the Multi-Tenancy page is the
 * canonical place to manage their tenant's Instances. Instance Admin
 * + Promoter are new Phase-3 roles and inherit the same allowlist as
 * Tenant Admin so they can navigate to the page (backend RBAC enforces
 * the per-row instance.read gate). */
/* ROLE_VISIBLE_IDS lived here: a hand-written map of role name to visible nav
 * ids. It was a second copy of the RBAC matrix kept in the frontend, and it
 * could not express a custom role at all — every custom role, and any built-in
 * role somebody forgot to add, fell through to [] and rendered an empty rail
 * with no error. Navigation now asks the same question the route guards ask,
 * against the user's server-issued effective permissions. See
 * data/routeGates.js. */

/* ────────────────────────────────────────────────────────────────
   COMPONENT
   ──────────────────────────────────────────────────────────────── */
export default function DashboardLayoutV2({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, currentRole, tenant, session, memberships, switchInstance, switchCustomer, switchTenant, hasPermission } = useAuth();
  /* Membership switching lives in useScopeSwitch — the one place that
   * behaviour exists. The Smart Agent Wizard's scope control calls the same
   * hook, so the guard, the request, the in-flight state, the error and the
   * clean-URL reload cannot drift apart between the two surfaces. */
  const {
    switching,
    error: switchError,
    switchTo: switchToMembership,
  } = useScopeSwitch();

  // RC-224 — Global SSE toast wire-up. Subscribes ONCE here in the app
  // shell to form_task_assigned events from the form-tasks SSE bus.
  // Auto-suppresses when the user is already on /forms/to-do (the page
  // refreshes its own list on the same event — toast would be noise).
  // See useFormTaskToastNotifier.js for the full contract.
  useFormTaskToastNotifier();

  // RC-227 — My Tasks badge live count. Bus singleton (refcounted, RC-224).
  const { count: myTasksPendingCount } = useMyTasksPendingCount();
  // 2026-07-04 — useFormsPendingCount previously consumed here for the
  // /forms badge is now unused. The hook file stays alongside the SSE
  // bus utilities for possible future callers, but nothing in this shell
  // reads its output anymore — /forms is author-only, My Tasks owns the
  // "waiting on you" model per operator directive.

  // V191 / Phase-6 — Instances visible to the caller in their current
  // Tenant. Loaded on user-menu open, cached for the menu lifetime.
  // Empty array until first open; loadInstances refreshes it.
  const [menuInstances, setMenuInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [instanceSwitching, setInstanceSwitching] = useState(false);
  const [instanceSwitchError, setInstanceSwitchError] = useState(null);

  // V208 / RBAC v2 — Customer picker for Platform Admin. Loaded on
  // user-menu open. Backend returns [] for non-Platform-Admin so the
  // picker auto-hides for everyone else.
  const [menuCustomers, setMenuCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  /* RC-137a — Scope picker (orgs + tenants) for the currently-pinned
   * customer. PA-only. Lazy-loaded when the user menu opens AND a
   * customer is pinned; refetched if the operator switches customer
   * mid-session. Shape: { customerName, orgs: [{id, name, tenants:[]}] }
   * or null when not loaded yet. */
  const [scopePicker, setScopePicker] = useState(null);
  const [scopePickerLoading, setScopePickerLoading] = useState(false);
  const [scopePickerError, setScopePickerError] = useState(null);
  /* In-flight tenantId during switchTenant — disables the row + greys
   * the entire section so a double-click can't fire two switches. */
  const [tenantSwitching, setTenantSwitching] = useState(null);
  const [tenantSwitchError, setTenantSwitchError] = useState(null);
  /* G584 — track which customer row is mid-switch so we can show a
   * "Switching…" affordance without locking the rest of the menu. Mirrors
   * the (boolean) instanceSwitching/switching patterns but stores the
   * customerId so per-row state can render correctly when N>2. */
  const [customerSwitching, setCustomerSwitching] = useState(null);
  const [customerSwitchError, setCustomerSwitchError] = useState(null);

  /**
   * G410 — Activate a different membership. Calls the auth context's
   * switchMembership, which POSTs /api/v1/auth/switch-membership; the
   * server invalidates the prior token via the DB-backed blacklist
   * (G408 / V179) and returns a fresh JWT bound to the chosen (org,
   * tenant, role) tuple. We then full-page reload so React-Query
   * caches refetch under the new scope.
   */
  const handleSwitchMembership = async (membershipId) => {
    const result = await switchToMembership(membershipId);
    // Only this surface's own business: the menu. The hook owns the guard, the
    // request, the error and the clean-URL reload (useScopeSwitch.js:63-79).
    if (result?.ok) setUserMenuOpen(false);
  };

  /**
   * V191 / Phase-6 — switch active Instance within the current Tenant.
   *
   * Calls AuthContext.switchInstance which delegates to
   * /api/v1/auth/switch-instance. The membership stays the same; only
   * the JWT's `iid` claim changes. Page reloads on success so every
   * resource-list refetches under the new Instance scope (Phase-3
   * RBAC's requirePermissionForScope re-resolves the effective role).
   */
  const handleSwitchInstance = async (instanceId) => {
    if (!instanceId) return;
    if (instanceId === session?.instanceId) {
      setUserMenuOpen(false);
      return;
    }
    setInstanceSwitching(true);
    setInstanceSwitchError(null);
    const result = await switchInstance(instanceId);
    setInstanceSwitching(false);
    if (!result.ok) {
      setInstanceSwitchError(result.error || 'Instance switch failed');
      return;
    }
    setUserMenuOpen(false);
    // G536 — Clean-URL reload mirrors handleSwitchMembership above.
    // Instance scope change also invalidates per-page selection state;
    // an instanceId in the URL from before the switch points to the
    // wrong Instance under the new pinning.
    window.location.href = window.location.pathname;
  };

  /**
   * V191 / Phase-6 — lazy-load Instances when the user menu opens.
   * Backend serves only Instances the caller can see under their
   * current Tenant scope (Phase-3 RBAC). Empty list → switcher hides.
   */
  const loadMenuInstances = React.useCallback(async () => {
    if (!session?.tenantId) { setMenuInstances([]); return; }
    setInstancesLoading(true);
    try {
      const res = await api.listInstancesForTenant(session.tenantId);
      // apiFetch returns {ok, data}; unwrap and coerce.
      if (res && res.ok === false) {
        setMenuInstances([]);
      } else {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        setMenuInstances(Array.isArray(payload) ? payload : []);
      }
    } catch (e) {
      console.warn('[Phase-6] listInstancesForTenant failed', e);
      setMenuInstances([]);
    } finally {
      setInstancesLoading(false);
    }
  }, [session?.tenantId]);

  /**
   * V208 / RBAC v2 — lazy-load customers when the user menu opens.
   * Backend returns [] for non-Platform-Admin so the picker hides
   * automatically for everyone else; no client-side role guard needed.
   */
  const loadMenuCustomers = React.useCallback(async () => {
    setCustomersLoading(true);
    try {
      const res = await api.listCustomers();
      if (res && res.ok === false) {
        setMenuCustomers([]);
      } else {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        const list = Array.isArray(payload) ? payload : [];
        // The quick-switch menu only offers ACTIVE customers. switchCustomer
        // rejects SUSPENDED/OFFBOARDED anyway, and the /customers management page
        // is where inactive customers are reactivated/audited/offboarded. The
        // shared GET /api/v1/customers returns every status (for that page), so
        // we filter here. (Missing status → treated as ACTIVE, defensive.)
        setMenuCustomers(list.filter((c) => (c.status ?? 'ACTIVE') === 'ACTIVE'));
      }
    } catch (e) {
      console.warn('[V208] listCustomers failed', e);
      setMenuCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  /**
   * RC-137a — Lazy-load the {orgs, tenants} tree for the currently-pinned
   * customer. PA-only; BE returns 403 for other roles and we short-circuit
   * the call on the client too so the picker doesn't flash for non-PAs.
   * Refetched whenever (a) the user menu opens or (b) the operator
   * switches customer (session.customerId changes) — covers the "pick
   * customer A, drill into A, then pick customer B" flow without
   * holding A's tree in stale state.
   */
  const loadScopePicker = React.useCallback(async () => {
    // Inlined role check (reference_useeffect_dep_tdz): `isPlatformAdmin`
    // is declared LATER in this component (~line 512); using it in the
    // dep array would TDZ at render time. currentRole is destructured
    // at the top of the component (line 338) so it's safe to read here.
    if (currentRole !== 'Platform Admin') { setScopePicker(null); return; }
    const cid = session?.customerId;
    if (!cid) { setScopePicker(null); return; }
    setScopePickerLoading(true);
    setScopePickerError(null);
    try {
      const res = await api.getScopePicker(cid);
      if (res && res.ok === false) {
        setScopePicker(null);
        setScopePickerError(res.error || 'Could not load orgs and tenants');
      } else {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        setScopePicker(payload || null);
      }
    } catch (e) {
      console.warn('[RC-137a] getScopePicker failed', e);
      setScopePicker(null);
      setScopePickerError(e?.message || String(e));
    } finally {
      setScopePickerLoading(false);
    }
  }, [currentRole, session?.customerId]);
  const { approvalInboxCount, loadApprovalInboxCount } = usePlatform();
  // RC-56 — previousScreen drives the "← Back to X" pill below.
  const { currentLabel, recentEntities, previousScreen } = useNavigation();
  // Default expanded on desktop, collapsed on mobile — below lg the sidebar is an
  // overlay drawer, so it must start closed on a phone rather than covering the app.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => (typeof window === 'undefined' ? true : window.innerWidth >= 1024)
  );
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  /* G578 — Two orthogonal axes (membership + instance) both collapsible
   * with chevron indicators. Default: Active Scope collapsed (operators
   * rarely change role mid-session), Active Instance expanded (the
   * common task — switching environments within current tenant). */
  const [scopeExpanded, setScopeExpanded] = useState(false);
  const [instanceExpanded, setInstanceExpanded] = useState(true);
  const wsRef = useRef(null);
  const userMenuRef = useRef(null);

  const isSuperAdmin = currentRole === 'Super Admin';
  // Phase-1 packaging — hide nav items for modules this customer isn't entitled to,
  // but ONLY when the customer is ENFORCING (observe = no UX change). Platform Admin
  // and fail-open (summary not loaded) always show everything.
  const { isUsable: isModuleUsable, enforcing: entEnforcing } = useEntitlements();
  const isPlatformAdmin = currentRole === 'Platform Admin';
  const navItemAllowed = (id) => {
    const m = NAV_ITEM_MODULE[id];
    return !entEnforcing || isPlatformAdmin || !m || isModuleUsable(m);
  };
  /* A role absent from the allowlist resolves to [] and the section filter
   * below then drops every item, so the whole rail renders empty with no error
   * anywhere — which is how 'Protect' shipped with no navigation at all. The
   * fallback stays (a blank rail beats a crash), but it no longer does it
   * quietly: an unknown role is a wiring mistake and says so once. */
  /* The identity every gate is decided against. hasPermission reads
     session.permissions, which the server builds from the DB-backed matrix —
     so a custom role is decided on its real rights like any built-in one. */
  const who = useMemo(() => ({ hasPermission, role: currentRole }), [hasPermission, currentRole]);

  /* V191 / Phase-6 — load Instances visible in the current Tenant so
   * the topbar scope breadcrumb can render the active Instance pill
   * without waiting for the user-menu to open. Re-runs when the
   * session.tenantId changes (membership switch). Safe-no-op when
   * the user is unauthenticated. */
  useEffect(() => {
    if (session?.tenantId) {
      loadMenuInstances();
    }
  }, [session?.tenantId, loadMenuInstances]);

  /* ── Approval inbox WebSocket — mirrors v1 wiring ───────────── */
  useEffect(() => {
    if (typeof loadApprovalInboxCount === 'function') {
      loadApprovalInboxCount().catch(() => {});
    }
    if (typeof connectApprovalWebSocket === 'function') {
      wsRef.current = connectApprovalWebSocket(() => {
        if (typeof loadApprovalInboxCount === 'function') {
          loadApprovalInboxCount().catch(() => {});
        }
      });
    }
    return () => { if (wsRef.current) disconnectApprovalWebSocket && disconnectApprovalWebSocket(); };
    // CRA build doesn't have the react-hooks plugin loaded, so the
    // `react-hooks/exhaustive-deps` directive trips ESLint with
    // "rule not found". Bare `eslint-disable-line` swallows whatever
    // rule actually fires here without depending on the plugin.
    // eslint-disable-next-line
  }, []);

  /* ── Click-outside for user menu ───────────────────────────── */
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  /* ── Filter NAV by role + nav-section flag ─────────────────── */
  const filteredNav = useMemo(() => {
    return NAV
      /* RC-89 — Gate the SYSTEM section on `isPlatformAdmin`, not
         `isSuperAdmin`. The old `superAdminOnly` field is preserved
         as a back-compat alias (a section with the old key still
         resolves correctly) but every section in NAV now uses
         `platformAdminOnly`. See the long comment on the System
         section above for the full rationale. */
      .filter((section) => (
        (!section.platformAdminOnly || isPlatformAdmin)
        && (!section.superAdminOnly || isSuperAdmin)
      ))
      .filter((section) => !section.flagKey || isNavSectionEnabled(section.flagKey))
      .map((section) => ({
        ...section,
        items: (section.items || []).filter((it) => navItemVisible(it.id, who) && navItemAllowed(it.id)),
        subGroups: (section.subGroups || [])
          .filter((sg) => !sg.flagKey || isNavSectionEnabled(sg.flagKey))
          .map((sg) => ({ ...sg, items: sg.items.filter((it) => navItemVisible(it.id, who) && navItemAllowed(it.id)) }))
          .filter((sg) => sg.items.length > 0),
      }))
      .filter((section) => section.items.length > 0 || (section.subGroups && section.subGroups.length > 0));
  }, [who, isSuperAdmin, entEnforcing, isPlatformAdmin]);

  const isPathActive = (path) => {
    if (!path) return false;
    if (path === '/overview' && (location.pathname === '/' || location.pathname === '/overview')) return true;
    /* R3.1 — Query-string-aware match. When a nav item's path embeds a
     * query (e.g. /agents?type=ExteLet for the Registrations link), the
     * link should highlight only when ALL the embedded query params
     * match the current URL. Without this, navigating to
     * /agents?type=ExteLet would either fail to highlight Registrations
     * (because pathname=/agents alone), or worse, BOTH Operate/Agentlets
     * AND Registrations would highlight ambiguously. */
    const qIdx = path.indexOf('?');
    if (qIdx >= 0) {
      const navPath  = path.slice(0, qIdx);
      const navQuery = new URLSearchParams(path.slice(qIdx + 1));
      const curQuery = new URLSearchParams(location.search);
      if (location.pathname !== navPath) return false;
      for (const [k, v] of navQuery.entries()) {
        if (curQuery.get(k) !== v) return false;
      }
      return true;
    }
    /* For plain paths, also require that the current URL has NO query
     * params that would otherwise be claimed by a sibling nav item with
     * an embedded query. Specifically: /agents (Operate/Agentlets) must
     * NOT match when the current URL is /agents?type=ExteLet (which
     * belongs to the Registrations link). Generalized below. */
    if (path === '/agents' && location.pathname === '/agents') {
      const curType = new URLSearchParams(location.search).get('type');
      if (curType) return false; // a typed-link sibling owns this
      return true;
    }
    return location.pathname.startsWith(path);
  };

  const renderBadge = (badgeKey) => {
    if (badgeKey === 'approvals' && approvalInboxCount > 0) {
      return (
        <span className="ml-auto text-[10px] font-bold px-1.5 py-px rounded-v2-pill bg-v2-brand text-white tabular-nums">
          {approvalInboxCount > 99 ? '99+' : approvalInboxCount}
        </span>
      );
    }
    // 2026-07-04 — Forms badge branch retired alongside /forms/to-do list
    // route. The 'forms' badgeKey is no longer wired to any nav item;
    // My Tasks below carries the same unified count using the amber
    // WARNING color (#d97706 — "caution without panic" per v2-tokens.css:67)
    // so pending assignments are impossible to miss on login sweep.
    //
    // RC-227 — My Tasks unified count (Forms + Attestations). Amber
    // WARNING color sits in the middle of the sidebar urgency ladder:
    // teal action = navigation cue, amber warning = work waiting on
    // you, red brand/danger = something wrong.
    if (badgeKey === 'myTasks' && myTasksPendingCount > 0) {
      return (
        <span
          className="ml-auto text-[10px] font-bold px-1.5 py-px rounded-v2-pill bg-v2-warning text-white tabular-nums"
          aria-label={`${myTasksPendingCount} pending task${myTasksPendingCount === 1 ? '' : 's'}`}
        >
          {myTasksPendingCount > 99 ? '99+' : myTasksPendingCount}
        </span>
      );
    }
    return null;
  };

  const renderDot = (dotKey) => {
    if (dotKey === 'killSwitch') {
      // Placeholder — real cascade state lives in PlatformContext later.
      return <span aria-hidden="true" className="ml-auto w-1.5 h-1.5 rounded-full bg-v2-success" />;
    }
    return null;
  };

  const NavItem = ({ item, indented }) => {
    const Icon = item.icon;
    const active = isPathActive(item.path);
    return (
      <button
        type="button"
        onClick={() => {
          navigate(item.path);
          // On mobile the sidebar is an overlay drawer — dismiss it after navigating.
          if (typeof window !== 'undefined' && window.innerWidth < 1024) setSidebarOpen(false);
        }}
        aria-current={active ? 'page' : undefined}
        className={
          'v2-focus-ring w-full flex items-center gap-2.5 px-2.5 py-1.5 text-[13px] rounded-md ' +
          'transition-colors duration-v2 mb-px ' +
          (indented ? 'ml-5 pl-2.5 border-l border-white/10' : '') + ' ' +
          (active
            ? 'bg-v2-sidebar-active text-v2-action-on-dark font-semibold'
            : 'text-v2-ink-inverse-secondary hover:bg-v2-sidebar-hover hover:text-v2-ink-inverse')
        }
      >
        {Icon && <Icon size={15} className="flex-shrink-0" aria-hidden="true" />}
        <span className="truncate">{item.label}</span>
        {renderBadge(item.badgeKey)}
        {renderDot(item.dotKey)}
      </button>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-v2-page text-v2-ink font-sans">
      {/* Mobile drawer backdrop — tap to dismiss. Hidden at lg where the rail is in-flow. */}
      {sidebarOpen && (
        <div
          aria-hidden="true"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        />
      )}

      {/* ───────────────────────── SIDEBAR ───────────────────────── */}
      <nav
        aria-label="Primary"
        className={
          'flex flex-col flex-shrink-0 bg-v2-sidebar overflow-y-auto z-50 ' +
          // Mobile (<lg): fixed slide-over drawer; does NOT occupy layout width.
          'fixed inset-y-0 left-0 w-[248px] shadow-v2-lg transition-transform duration-v2-lg ease-v2 ' +
          (sidebarOpen ? 'translate-x-0' : '-translate-x-full') + ' ' +
          // lg+: back in the flex flow as the expand/collapse rail (desktop behaviour unchanged).
          'lg:static lg:z-auto lg:shadow-none lg:translate-x-0 lg:transition-[width] ' +
          (sidebarOpen ? 'lg:w-[248px]' : 'lg:w-[64px]')
        }
      >
        <div className="flex items-center gap-2.5 px-5 pt-4 pb-3 border-b border-white/8">
          <button
            type="button"
            onClick={() => navigate('/overview')}
            aria-label="Prove7 Control Vector home"
            className="v2-focus-ring flex items-center gap-2.5"
          >
            {/* Full horizontal lockup: chevron mark, then the wordmark. Both are
              * the -light (white artwork) files — these assets are named for the
              * colour of the mark, not the surface, so the navy rail takes -light.
              *
              * 30px is a deliberate floor. The mark is a set of closely spaced
              * concentric strokes; below roughly 24px they stop resolving as
              * separate lines and read as a grey smudge. The 192px source gives
              * better than 3x DPR headroom at this size.
              *
              * aria-hidden + empty alt: the wordmark beside it already carries the
              * accessible name, and the button has its own aria-label, so
              * announcing the mark would just repeat the product name twice. */}
            <img
              src="/brand/mark-light.png"
              alt=""
              aria-hidden="true"
              width="30" height="25"
              className="flex-shrink-0"
              style={{ width: 30, height: 'auto' }}
            />
            <span className="text-left">
              {/* The stylized PROVE7 wordmark replaces the plain text label. The
                * -light file is the WHITE artwork — these assets are named for the
                * colour of the mark, not the surface, so the navy sidebar takes
                * -light. Source is 600px wide for a 92px slot, so it stays crisp
                * to 3x DPR. The sub-line is "Control Vector" only —
                * "Governed Execution Layer" wrapped to two lines in a 272px rail
                * and was dropped rather than shrunk. The full product name still
                * reaches assistive tech via the button's aria-label and this alt
                * text, and whitespace-nowrap keeps the sub-line on one line if the
                * rail is ever narrowed further. */}
              <img
                src="/brand/wordmark-light.png"
                alt="Prove7 Control Vector"
                width="92" height="18"
                className="block"
                style={{ width: 92, height: 'auto' }}
              />
              <span className="block text-[9.5px] uppercase tracking-wider text-v2-ink-inverse-muted mt-1 whitespace-nowrap">Control Vector</span>
            </span>
          </button>
        </div>

        {filteredNav.map((section) => (
          <div key={section.label} className="px-3 pt-4 pb-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-v2-ink-inverse-muted px-2.5 pb-2">
              {section.label}
            </div>
            {section.items.map((item) => (
              <NavItem key={item.id} item={item} />
            ))}
            {section.subGroups && section.subGroups.map((sg) => (
              <div key={sg.label} className="mt-1">
                <div className="text-[9.5px] uppercase tracking-wider font-semibold text-v2-ink-inverse-muted px-2.5 pt-2 pb-1.5 ml-5">
                  {sg.label}
                </div>
                {sg.items.map((item) => (
                  <NavItem key={item.id} item={item} indented />
                ))}
              </div>
            ))}
          </div>
        ))}

        {/* Active-scope footer.
         *
         * G418 — Show the ACTIVE membership's tenant name (and org +
         * role), not the user.tenantId. Pre-G418 this read from
         * `tenant?.name` which was sourced from PlatformContext's
         * cached tenant lookup — that only refreshed on full login and
         * stayed stale through a runtime membership switch. The active
         * membership is the authoritative source: it changes the moment
         * the user clicks "Switch to ..." in the avatar menu and is
         * persisted on the JWT itself (G406 `mid` claim).
         *
         * Resolution order:
         *  1. The membership whose membershipId matches session.membershipId
         *  2. Fall back to the user's primary tenant (legacy display)
         *  3. Fall back to "Member" placeholder
         */}
        <div className="mt-auto px-4 py-4 border-t border-white/8 flex items-center gap-2.5">
          {(() => {
            const activeMembership = Array.isArray(memberships)
              ? memberships.find((m) => m.membershipId === session?.membershipId)
              : null;
            const roleLabel = activeMembership?.role || session?.role || currentRole || 'Member';

            // V208 — Platform Admin chip. When the active membership is the
            // platform-scope sentinel, render a "Platform · All customers"
            // affordance instead of leaking the sentinel org name. The
            // backend marks platform-scope memberships with
            // isPlatformScope=true (see AuthService.getUserMemberships).
            // Also defends against legacy data where the sentinel name leaks
            // through despite the flag.
            const isPlatformScope = !!activeMembership?.isPlatformScope
              || activeMembership?.orgName === '__platform__';
            if (isPlatformScope) {
              return (
                <>
                  <div aria-hidden="true" className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-[12px]" style={{ background: 'rgba(124, 58, 237, .25)', color: '#c4b5fd' }}>
                    PA
                  </div>
                  <div className="flex-1 min-w-0 text-[12px]">
                    <div className="font-semibold text-v2-ink-inverse truncate">Platform</div>
                    <div className="text-v2-ink-inverse-muted text-[10.5px] truncate">{roleLabel} · All customers</div>
                  </div>
                </>
              );
            }

            const tenantName = activeMembership?.tenantName
              || activeMembership?.orgName
              || tenant?.name
              || 'Member';
            // Super Admin is CUSTOMER-scoped — show "Super Admin · <Customer>"
            // rather than an org name (which understated the actual breadth of
            // their authority across the whole customer). Other scoped roles
            // (Org/Tenant/Instance Admin) keep the org name.
            const scopeName = roleLabel === 'Super Admin'
              ? (activeMembership?.customerName || activeMembership?.orgName)
              : activeMembership?.orgName;
            const subtitle = scopeName && scopeName !== tenantName
              ? `${roleLabel} · ${scopeName}`
              : roleLabel;
            return (
              <>
                <div aria-hidden="true" className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-[12px]" style={{ background: 'rgba(124, 58, 237, .25)', color: '#c4b5fd' }}>
                  {tenantName.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0 text-[12px]">
                  <div className="font-semibold text-v2-ink-inverse truncate">{tenantName}</div>
                  <div className="text-v2-ink-inverse-muted text-[10.5px] truncate">{subtitle}</div>
                </div>
              </>
            );
          })()}
        </div>
      </nav>

      {/* ─────────────────────── MAIN COLUMN ─────────────────────── */}
      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        <header className="flex items-center gap-2 sm:gap-3 px-3 sm:px-6 h-14 bg-v2-surface border-b border-v2-border-subtle flex-shrink-0 min-w-0">
          <button
            type="button"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            className="v2-focus-ring w-8 h-8 rounded-v2-sm flex items-center justify-center text-v2-ink-secondary hover:bg-v2-muted hover:text-v2-ink transition-colors duration-v2"
          >
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>

          <GlobalSearchInline />

          {/* V191 / Phase-6 — Scope context breadcrumb.
           *
           * Slim Org → Tenant → Instance chip in the topbar. Renders only
           * when the user has at least one membership (always true after
           * login). Clicking the chip jumps to the Multi-Tenancy page
           * with a deep-link pre-selecting the current scope. The
           * Instance pill is omitted when session.instanceId is null
           * (pre-Phase-4 tokens) so the chip stays meaningful. */}
          {session?.tenantId && (() => {
            const activeMembership = Array.isArray(memberships)
              ? memberships.find((m) => m.membershipId === session?.membershipId)
              : null;
            /* RC-137f — Name resolution order:
             *   1. activeMembership.{org,tenant}Name — works for TA/OA
             *      whose JWT was minted from a real membership row.
             *   2. session.{org,tenant,customer}Name — server-derived
             *      from the JWT's active tuple. Works for Platform Admin
             *      after switchTenant when (1) is empty because PA's
             *      membership row points at the platform sentinel.
             *   3. tenant?.* legacy fallback for non-V208 sessions.
             *   4. UUID slice as last-resort (operator should never see
             *      this post-RC-137f; if they do it means /session
             *      lookup returned empty — file a follow-up).
             *
             * V208 — never expose the __platform__ sentinel name in the
             * breadcrumb. Defensive guard at render time too. */
            const rawOrgName = activeMembership?.orgName || session?.orgName || tenant?.orgName;
            const orgName = rawOrgName === '__platform__' ? null : rawOrgName;
            const tenantName = activeMembership?.tenantName
              || session?.tenantName
              || tenant?.name
              || session?.tenantId?.slice(0, 8);
            const customerName = session?.customerName || null;
            const activeInstance = menuInstances.find((i) => i.id === session?.instanceId);
            const params = new URLSearchParams();
            if (session?.orgId) params.set('orgId', session.orgId);
            if (session?.tenantId) params.set('tenantId', session.tenantId);
            if (session?.instanceId) params.set('instanceId', session.instanceId);
            const href = `/multi-tenancy${params.toString() ? '?' + params.toString() : ''}`;
            return (
              <button
                type="button"
                onClick={() => navigate(href)}
                className="v2-focus-ring hidden lg:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-v2-sm text-[12px] text-v2-ink-secondary bg-v2-muted hover:bg-v2-surface hover:text-v2-ink border border-transparent hover:border-v2-border-default transition-colors duration-v2"
                title="Multi-Tenancy — current scope"
                aria-label="Scope context — opens Multi-Tenancy"
              >
                <Building2 size={12} className="text-v2-ink-muted" aria-hidden="true" />
                {/* RC-137f — Customer leads the chip when known (typically
                    for PA who's pinned into a customer; TA/OA whose JWT
                    is bound to a single customer skip this since their
                    customer context is implicit). */}
                {customerName && (
                  <>
                    <span className="truncate max-w-[100px] text-v2-ink-muted">{customerName}</span>
                    <span className="text-v2-ink-muted" aria-hidden="true">/</span>
                  </>
                )}
                {orgName && (
                  <>
                    <span className="truncate max-w-[100px]">{orgName}</span>
                    <span className="text-v2-ink-muted" aria-hidden="true">/</span>
                  </>
                )}
                <span className="truncate max-w-[120px] font-medium">{tenantName}</span>
                {activeInstance && (
                  <>
                    <span className="text-v2-ink-muted" aria-hidden="true">/</span>
                    <span className="truncate max-w-[100px]">{activeInstance.name}</span>
                    <span className={
                      'text-[9.5px] uppercase tracking-wider font-semibold px-1 py-px rounded ' +
                      (activeInstance.isProduction
                        ? 'bg-v2-danger-soft text-v2-danger'
                        : activeInstance.tier === 'STAGING' ? 'bg-v2-warning-soft text-v2-warning'
                        : activeInstance.tier === 'DEV' ? 'bg-v2-cyan-soft text-v2-cyan'
                        : 'bg-v2-stone-100 text-v2-ink-muted')
                    }>
                      {activeInstance.tier}
                    </span>
                  </>
                )}
              </button>
            );
          })()}

          <div className="ml-auto flex items-center gap-2">
            {/* Across to the other surface. The wizard is a route in this same
                router — it simply renders without this shell — so this is a
                push. A real navigation here would discard the mounted app and
                re-run its whole start-up to show a page it already has. */}
            <button
              type="button"
              onClick={() => navigate('/agents/smartwizard')}
              title="The simple surface: name an agent, set its limits, watch it run"
              className="inline-flex items-center gap-1.5 rounded-md border border-v2-border
                         px-2.5 py-1 text-[12px] font-semibold text-v2-ink-secondary
                         hover:bg-v2-muted hover:text-v2-ink"
            >
              Smart Wizard
            </button>
            {/* Kill switch indicator — links to Admin Center → Inference */}
            <button
              type="button"
              onClick={() => navigate('/admin-center')}
              aria-label="Kill switch ready · open Admin Center"
              className="v2-focus-ring inline-flex items-center gap-2 px-3 h-8 rounded-v2-sm text-[12px] font-medium border border-transparent text-v2-ink-secondary bg-v2-muted hover:border-v2-brand hover:text-v2-brand hover:bg-v2-brand-soft transition-colors duration-v2"
            >
              <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-v2-success" />
              <span className="hidden sm:inline">Kill switch ready</span>
            </button>

            {/* Notifications (placeholder) */}
            <button type="button" aria-label="Notifications" className="v2-focus-ring w-8 h-8 rounded-v2-sm flex items-center justify-center text-v2-ink-secondary hover:bg-v2-muted hover:text-v2-ink transition-colors duration-v2 relative">
              <Bell size={16} aria-hidden="true" />
              {approvalInboxCount > 0 && (
                <span aria-hidden="true" className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-v2-brand rounded-full" />
              )}
            </button>

            {/* Help */}
            <button type="button" aria-label="Help" className="v2-focus-ring w-8 h-8 rounded-v2-sm flex items-center justify-center text-v2-ink-secondary hover:bg-v2-muted hover:text-v2-ink transition-colors duration-v2">
              <HelpCircle size={16} aria-hidden="true" />
            </button>

            {/* User chip + menu */}
            <div ref={userMenuRef} className="relative">
              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen((v) => {
                    const next = !v;
                    if (next) {
                      loadMenuInstances();
                      // V208 — load customer list for the Platform Admin picker.
                      // Backend returns [] for non-PA so this is a cheap no-op
                      // for regular operators.
                      loadMenuCustomers();
                      // RC-137a — load the {orgs, tenants} tree for the
                      // currently-pinned customer (PA-only; bails on
                      // non-PA or no-customer-pinned).
                      loadScopePicker();
                    }
                    return next;
                  });
                }}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                className="v2-focus-ring inline-flex items-center gap-2 px-1 pr-3 h-8 rounded-v2-pill bg-v2-muted hover:bg-v2-elevated transition-colors duration-v2"
              >
                <span aria-hidden="true" className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-white text-[10.5px]" style={{ background: 'linear-gradient(135deg, var(--v2-action), var(--v2-accent-trust))' }}>
                  {(user?.name || 'U').slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden sm:inline text-[12px] font-medium text-v2-ink truncate max-w-[120px]">{user?.name || user?.email || 'User'}</span>
                <ChevronDown size={12} className="text-v2-ink-muted" aria-hidden="true" />
              </button>
              {userMenuOpen && (
                <div role="menu" className="absolute right-0 top-[calc(100%+6px)] z-30 min-w-[260px] bg-v2-surface border border-v2-border rounded-v2-md shadow-v2-lg py-1.5 slide-down">
                  <div className="px-3 py-2 border-b border-v2-border-subtle">
                    <div className="text-[12px] font-semibold text-v2-ink truncate">{user?.name || user?.email || 'User'}</div>
                    <div className="text-[11px] text-v2-ink-muted truncate">{user?.email}</div>
                  </div>

                  {/* G410 — Active scope + membership switcher.
                   *
                   * Rendered only when the user has at least one membership
                   * (every authenticated user does, after V178 backfill).
                   * Hides the section entirely if there's only one
                   * membership and we have nothing to switch to — keeps
                   * the menu minimal for single-tenant operators.
                   *
                   * V208 — when the active membership is platform-scope
                   * (Platform Admin not yet switched into a customer), we
                   * render a special "Platform · All customers" label
                   * instead of leaking the __platform__ sentinel name.
                   * Backend marks platform-scope memberships with
                   * isPlatformScope=true; defensive sentinel-name match
                   * covers stale clients. */}
                  {Array.isArray(memberships) && memberships.length > 0 && (() => {
                    const active = memberships.find((m) => m.membershipId === session?.membershipId);
                    const activeIsPlatformScope = !!active?.isPlatformScope
                      || active?.orgName === '__platform__';
                    const activeOrgName = active?.orgName === '__platform__' ? null : active?.orgName;
                    const activeTitle = activeIsPlatformScope
                      ? 'Platform'
                      : (active?.tenantName
                          || activeOrgName
                          || (session?.tenantId ? session.tenantId.slice(0, 8) : 'Unknown'));
                    // V208 / Phase E-6 — surface stacked roles. With multi-
                    // role-per-scope (V208 6-col UNIQUE NULLS NOT DISTINCT
                    // on user_org_roles), a user can hold Developer +
                    // Approver + Compliance in the SAME scope. The active
                    // JWT carries one role at a time; the others are
                    // switchable via the picker below. Surface a "+N more
                    // roles" hint when stacked roles exist so the operator
                    // knows their effective privilege isn't just what's in
                    // the active chip.
                    const sameScopeStacked = (active && Array.isArray(memberships))
                      ? memberships.filter((m) =>
                          m.membershipId !== active.membershipId
                          && (m.orgId || null) === (active.orgId || null)
                          && (m.tenantId || null) === (active.tenantId || null)
                          && (m.instanceId || null) === (active.instanceId || null)
                        )
                      : [];
                    const stackedHint = sameScopeStacked.length > 0
                      ? ` · +${sameScopeStacked.length} stacked role${sameScopeStacked.length === 1 ? '' : 's'}`
                      : '';
                    // Super Admin is customer-scoped — render the customer name,
                    // not the org. Falls back to org name for legacy membership
                    // rows that predate customerName enrichment.
                    const activeRole = session?.role || currentRole;
                    const activeScopeName = activeRole === 'Super Admin'
                      ? (active?.customerName || activeOrgName)
                      : activeOrgName;
                    const activeSubtitle = activeIsPlatformScope
                      ? `${activeRole} · All customers${stackedHint}`
                      : `${activeRole}${activeScopeName ? ' · ' + activeScopeName : ''}${stackedHint}`;
                    return (
                    <div className="border-b border-v2-border-subtle py-1">
                      {/* G578 — Collapsible Membership axis. Default collapsed
                       * (operators rarely re-pick role mid-session); when
                       * collapsed the header still shows the active scope
                       * summary so the user can confirm at a glance. */}
                      <button
                        type="button"
                        onClick={() => setScopeExpanded((v) => !v)}
                        className="v2-focus-ring w-full flex items-center gap-1.5 px-3 pt-1.5 pb-1 hover:bg-v2-muted/40 transition-colors"
                        aria-expanded={scopeExpanded}
                        aria-controls="scope-axis-body"
                      >
                        {scopeExpanded
                          ? <ChevronDown size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />
                          : <ChevronRight size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />}
                        <Building2 size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />
                        <span className="text-[10px] uppercase tracking-wide font-semibold text-v2-ink-muted">
                          Membership
                        </span>
                        {!scopeExpanded && (
                          <span className="ml-auto text-[11px] text-v2-ink-muted truncate max-w-[60%]" title={`${activeTitle} · ${activeSubtitle}`}>
                            {activeTitle}
                          </span>
                        )}
                      </button>
                      {scopeExpanded && (
                        <div id="scope-axis-body">
                          <div className="px-3 py-1.5">
                            <div className="flex items-center gap-1.5">
                              <Building2 size={11} className="text-v2-ink-muted" aria-hidden="true" />
                              <span className="text-[12px] font-medium text-v2-ink truncate">
                                {activeTitle}
                              </span>
                            </div>
                            <div className="text-[11px] text-v2-ink-muted truncate">
                              {activeSubtitle}
                            </div>
                          </div>
                          {memberships.length > 1 && (
                            <>
                              <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide font-semibold text-v2-ink-muted">
                                Switch role / tenant
                              </div>
                              <div className="px-1 pb-1 max-h-[200px] overflow-y-auto">
                                {memberships
                                  .filter((m) => m.membershipId !== session?.membershipId)
                                  .map((m) => (
                                    <button
                                      key={m.membershipId}
                                      type="button"
                                      role="menuitem"
                                      disabled={switching}
                                      onClick={() => handleSwitchMembership(m.membershipId)}
                                      className="v2-focus-ring w-full flex flex-col items-start gap-0 px-2.5 py-1.5 rounded-v2-sm text-left hover:bg-v2-muted disabled:opacity-50 transition-colors duration-v2"
                                    >
                                      <div className="flex items-center gap-1.5 w-full">
                                        <Building2 size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />
                                        <span className="text-[12px] font-medium text-v2-ink truncate">
                                          {m.tenantName || m.orgName || (m.tenantId ? m.tenantId.slice(0, 8) : 'Unknown')}
                                        </span>
                                      </div>
                                      <span className="text-[11px] text-v2-ink-muted truncate pl-[18px]">
                                        {m.role}{m.orgName ? ` · ${m.orgName}` : ''}
                                      </span>
                                    </button>
                                  ))}
                              </div>
                              {switchError && (
                                <div className="px-3 py-1 text-[11px] text-v2-danger" role="alert">
                                  {switchError}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {/* V208 / RBAC v2 — Customer picker.
                   *
                   * Rendered only when the backend returns customers (Platform
                   * Admin sees the full ACTIVE customer list; non-PA gets [] so
                   * this section auto-hides). Each row currently navigates to
                   * /multi-tenancy with the customer pre-selected — Phase D
                   * will replace this with a real switch-into-customer JWT
                   * re-issue endpoint, after which clicking a row will scope
                   * the entire session to that customer.
                   */}
                  {(customersLoading || menuCustomers.length > 0) && (
                    <div className="border-b border-v2-border-subtle py-1">
                      <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide font-semibold text-v2-ink-muted">
                        Customers
                      </div>
                      {customersLoading ? (
                        <div className="px-3 py-1.5 text-[11px] text-v2-ink-muted">Loading…</div>
                      ) : (
                        <div className="px-1 pb-1 max-h-[200px] overflow-y-auto">
                          {/* G584 — visually mark the currently-active
                            * customer + wire the click to AuthContext's
                            * switchCustomer (Phase D — replaces the prior
                            * navigate-to-multi-tenancy placeholder). The
                            * active row is non-interactive, gets a teal
                            * background + ring + green dot + "Current"
                            * pill so a glance at the menu answers
                            * "which customer am I in right now?". */}
                          {menuCustomers.map((c) => {
                            const isActive = !!session?.customerId && c.id === session.customerId;
                            const isSwitching = customerSwitching === c.id;
                            const isInactive = c.status && c.status !== 'ACTIVE';
                            return (
                              <button
                                key={c.id}
                                type="button"
                                role="menuitem"
                                disabled={isActive || isSwitching || isInactive}
                                aria-current={isActive ? 'true' : undefined}
                                onClick={async () => {
                                  if (isActive || isSwitching) return;
                                  setCustomerSwitching(c.id);
                                  setCustomerSwitchError(null);
                                  try {
                                    const res = await switchCustomer(c.id);
                                    if (res && res.ok === false) {
                                      setCustomerSwitchError(res.error || 'Switch failed');
                                      setCustomerSwitching(null);
                                      return;
                                    }
                                    setUserMenuOpen(false);
                                    // Full-page reload so every surface
                                    // re-resolves under the new cid claim,
                                    // mirroring switch-membership /
                                    // switch-instance behaviour.
                                    window.location.href = '/overview';
                                  } catch (e) {
                                    setCustomerSwitchError(e?.message || String(e));
                                    setCustomerSwitching(null);
                                  }
                                }}
                                title={isActive
                                  ? `${c.displayName || c.name} is your current scope`
                                  : `Switch into ${c.displayName || c.name}`}
                                className={
                                  'v2-focus-ring w-full flex flex-col items-start gap-0 px-2.5 py-1.5 rounded-v2-sm text-left transition-colors duration-v2 '
                                  + (isActive
                                    ? 'cursor-default'
                                    : 'hover:bg-v2-muted disabled:opacity-50')
                                }
                              >
                                <div className="flex items-center gap-1.5 w-full">
                                  {/* G584/G593 — active customer indicator is
                                    * a single green dot replacing the
                                    * Building2 icon. No ring, no "Current"
                                    * pill, no teal text — those read as
                                    * pointer/arrow chrome and got reported
                                    * as a stuck visual. Subtle dot keeps
                                    * the row's normal shape so the menu
                                    * doesn't reflow when scope changes. */}
                                  {isActive
                                    ? <span aria-hidden="true" className="w-2 h-2 rounded-full bg-v2-success flex-shrink-0" />
                                    : <Building2 size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />}
                                  <span className="text-[12px] font-medium text-v2-ink truncate">
                                    {c.displayName || c.name}
                                  </span>
                                  {isSwitching && (
                                    <span className="ml-auto text-[10px] uppercase tracking-wide text-v2-ink-muted">
                                      Switching…
                                    </span>
                                  )}
                                </div>
                                <span className="text-[11px] text-v2-ink-muted truncate pl-[18px]">
                                  {c.name}{isInactive ? ` · ${c.status}` : ''}
                                </span>
                              </button>
                            );
                          })}
                          {customerSwitchError && (
                            <div className="px-3 py-1 text-[11px] text-v2-danger" role="alert">
                              {customerSwitchError}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* RC-137a — Orgs + Tenants picker (Platform Admin only).
                   *
                   * Visible after a PA has picked a customer. Lists every
                   * Org under that customer with each Org's tenants nested
                   * underneath as clickable rows. Picking a tenant calls
                   * switchTenant(tid) — BE re-issues the JWT with new tid
                   * + auto-derived oid + auto-derived cid; we full-reload
                   * to /overview so every page re-resolves under the new
                   * tenant pin (mirrors switchCustomer/switchInstance).
                   *
                   * Hidden when:
                   *   - caller is not Platform Admin
                   *   - no customer is pinned (operator needs to pick one
                   *     above first; picker section renders empty otherwise)
                   *   - customer has no orgs (defensive — BE handles)
                   *
                   * Currently-pinned tenant gets a green dot + "Current"
                   * affordance, matching the Customers section style. */}
                  {isPlatformAdmin && session?.customerId && (scopePickerLoading || scopePicker) && (
                    <div className="border-b border-v2-border-subtle py-1">
                      <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide font-semibold text-v2-ink-muted">
                        Orgs &amp; Tenants
                        {scopePicker?.customerName && (
                          <span className="ml-1 normal-case font-normal text-v2-ink-muted/80">
                            in {scopePicker.customerName}
                          </span>
                        )}
                      </div>
                      {scopePickerLoading ? (
                        <div className="px-3 py-1.5 text-[11px] text-v2-ink-muted">Loading…</div>
                      ) : scopePickerError ? (
                        <div className="px-3 py-1.5 text-[11px] text-v2-danger">
                          Could not load: {scopePickerError}
                        </div>
                      ) : (
                        <div className="px-1 pb-1 max-h-[260px] overflow-y-auto">
                          {(scopePicker?.orgs || []).length === 0 ? (
                            <div className="px-3 py-1.5 text-[11px] text-v2-ink-muted">
                              No orgs found for this customer.
                            </div>
                          ) : (
                            (scopePicker?.orgs || []).map((org) => (
                              <div key={org.id} className="mb-1.5">
                                <div className="px-2.5 pt-1 pb-0.5 text-[10px] uppercase tracking-wide font-medium text-v2-ink-muted/80">
                                  {org.name}
                                </div>
                                {(org.tenants || []).length === 0 ? (
                                  <div className="px-3 py-1 text-[11px] text-v2-ink-muted italic">
                                    (no tenants in this org)
                                  </div>
                                ) : (
                                  org.tenants.map((t) => {
                                    const isActive = !!session?.tenantId && t.id === session.tenantId;
                                    const isSwitching = tenantSwitching === t.id;
                                    return (
                                      <button
                                        key={t.id}
                                        type="button"
                                        role="menuitem"
                                        disabled={isActive || isSwitching}
                                        aria-current={isActive ? 'true' : undefined}
                                        onClick={async () => {
                                          if (isActive || isSwitching) return;
                                          setTenantSwitching(t.id);
                                          setTenantSwitchError(null);
                                          try {
                                            const res = await switchTenant(t.id);
                                            if (res && res.ok === false) {
                                              setTenantSwitchError(res.error || 'Switch failed');
                                              setTenantSwitching(null);
                                              return;
                                            }
                                            setUserMenuOpen(false);
                                            // Full-page reload so every surface
                                            // re-resolves under the new tid claim,
                                            // mirroring switchCustomer/switchInstance.
                                            window.location.href = '/overview';
                                          } catch (e) {
                                            setTenantSwitchError(e?.message || String(e));
                                            setTenantSwitching(null);
                                          }
                                        }}
                                        title={isActive
                                          ? `${t.name} is your pinned tenant`
                                          : `Pin to tenant ${t.name} (org: ${org.name})`}
                                        className={
                                          'v2-focus-ring w-full flex items-center gap-1.5 px-2.5 py-1.5 rounded-v2-sm text-left transition-colors duration-v2 '
                                          + (isActive
                                            ? 'cursor-default bg-v2-accent-trust-soft ring-1 ring-v2-accent-trust'
                                            : 'hover:bg-v2-muted disabled:opacity-50')
                                        }
                                      >
                                        {isActive
                                          ? <span aria-hidden="true" className="w-2 h-2 rounded-full bg-v2-success flex-shrink-0" />
                                          : <span aria-hidden="true" className="w-2 h-2 rounded-full border border-v2-border-default flex-shrink-0" />}
                                        <span className="text-[12px] text-v2-ink truncate flex-1">
                                          {t.name}
                                        </span>
                                        {isActive && (
                                          <span className="text-[9.5px] uppercase tracking-wide font-semibold text-v2-success">
                                            Current
                                          </span>
                                        )}
                                        {isSwitching && (
                                          <span className="text-[10px] text-v2-ink-muted">Switching…</span>
                                        )}
                                      </button>
                                    );
                                  })
                                )}
                              </div>
                            ))
                          )}
                          {tenantSwitchError && (
                            <div className="px-3 py-1 mt-1 text-[11px] text-v2-danger">
                              {tenantSwitchError}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* V191 / Phase-6 — Instance switcher within current Tenant.
                   *
                   * Lists every Instance the caller can see in their current
                   * Tenant scope. Selecting one calls /api/v1/auth/switch-instance
                   * which re-issues the JWT with a new `iid` claim and
                   * blacklists the prior token. Page reloads on success so
                   * Phase-3 RBAC re-resolves the effective role at the new
                   * (tenant, instance) tuple.
                   *
                   * Hidden when:
                   *   - menu hasn't loaded instances yet (loading shown)
                   *   - caller has < 2 visible instances (nothing to switch to) */}
                  {(instancesLoading || menuInstances.length >= 1) && (() => {
                    /* G578 — Collapsible Environment axis. Default expanded
                     * (switching between Dev/Staging/Prod is the most common
                     * task during a session); the collapsed header still
                     * surfaces the active environment so it's visible at a
                     * glance even without expanding. */
                    const activeInstance = menuInstances.find((i) => i.id === session?.instanceId);
                    const collapsedSummary = instancesLoading
                      ? 'Loading…'
                      : activeInstance
                        ? `${activeInstance.name} · ${activeInstance.tier}`
                        : (session?.instanceId ? 'Not visible' : 'None');
                    return (
                    <div className="border-b border-v2-border-subtle py-1">
                      <button
                        type="button"
                        onClick={() => setInstanceExpanded((v) => !v)}
                        className="v2-focus-ring w-full flex items-center gap-1.5 px-3 pt-1.5 pb-1 hover:bg-v2-muted/40 transition-colors"
                        aria-expanded={instanceExpanded}
                        aria-controls="instance-axis-body"
                      >
                        {instanceExpanded
                          ? <ChevronDown size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />
                          : <ChevronRight size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />}
                        <Boxes size={11} className="text-v2-ink-muted flex-shrink-0" aria-hidden="true" />
                        <span className="text-[10px] uppercase tracking-wide font-semibold text-v2-ink-muted">
                          Current environment
                        </span>
                        {!instanceExpanded && (
                          <span className="ml-auto text-[11px] text-v2-ink-muted truncate max-w-[60%]" title={collapsedSummary}>
                            {collapsedSummary}
                          </span>
                        )}
                      </button>
                      {instanceExpanded && (
                        <div id="instance-axis-body">
                          <div className="px-3 py-1.5">
                            {instancesLoading ? (
                              <div className="text-[11px] text-v2-ink-muted">Loading…</div>
                            ) : activeInstance ? (
                              <div className="flex items-center gap-1.5">
                                <span aria-hidden="true" className="w-2.5 h-2.5 rounded-full bg-v2-success" />
                                <span className="text-[12px] font-medium text-v2-ink truncate">
                                  {activeInstance.name}
                                </span>
                                <span className="text-[10px] uppercase tracking-wide text-v2-ink-muted">
                                  {activeInstance.tier}
                                </span>
                              </div>
                            ) : (
                              <div className="text-[11px] text-v2-ink-muted">
                                {session?.instanceId ? 'Instance not visible to this session' : 'No Instance selected'}
                              </div>
                            )}
                          </div>
                          {menuInstances.length > 1 && (
                            <>
                              <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide font-semibold text-v2-ink-muted">
                                Switch environment
                              </div>
                              <div className="px-1 pb-1 max-h-[200px] overflow-y-auto">
                                {menuInstances
                                  .filter((i) => i.id !== session?.instanceId)
                                  .map((i) => (
                                    <button
                                      key={i.id}
                                      type="button"
                                      role="menuitem"
                                      disabled={instanceSwitching}
                                      onClick={() => handleSwitchInstance(i.id)}
                                      className="v2-focus-ring w-full flex flex-col items-start gap-0 px-2.5 py-1.5 rounded-v2-sm text-left hover:bg-v2-muted disabled:opacity-50 transition-colors duration-v2"
                                      title={`Switch to ${i.name} (${i.tier})`}
                                    >
                                      <div className="flex items-center gap-1.5 w-full">
                                        {i.isProduction
                                          ? <span aria-hidden="true" className="w-2 h-2 rounded-full bg-v2-danger" />
                                          : <span aria-hidden="true" className="w-2 h-2 rounded-full bg-v2-action-soft border border-v2-action" />}
                                        <span className="text-[12px] font-medium text-v2-ink truncate">{i.name}</span>
                                        <span className="text-[10px] uppercase tracking-wide text-v2-ink-muted ml-auto">{i.tier}</span>
                                      </div>
                                      {i.description && (
                                        <span className="text-[11px] text-v2-ink-muted truncate pl-[14px]">
                                          {i.description}
                                        </span>
                                      )}
                                    </button>
                                  ))}
                              </div>
                              {instanceSwitchError && (
                                <div className="px-3 py-1 text-[11px] text-v2-danger" role="alert">
                                  {instanceSwitchError}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  <button
                    type="button"
                    role="menuitem"
                    /* DEF-AUTH-LOGOUT-FE-SKIPS-SERVER-001 (defence in
                     * depth) — the prior handler called logout() without
                     * awaiting it and navigated immediately. If the user
                     * closed the tab between click and fetch completion,
                     * the server-side /logout call never landed and the
                     * session lived for 8h. Await the logout so the
                     * Set-Cookie clear + watermark bump are guaranteed
                     * to commit before we navigate. Mirrors the pattern
                     * already used by the v1 DashboardLayout. */
                    onClick={async () => {
                      setUserMenuOpen(false);
                      try {
                        if (logout) await logout();
                      } finally {
                        navigate('/login');
                      }
                    }}
                    className="v2-focus-ring w-full flex items-center gap-2 px-3 py-2 mx-1 rounded-v2-sm text-[12.5px] text-v2-ink hover:bg-v2-muted transition-colors duration-v2"
                    style={{ width: 'calc(100% - 0.5rem)' }}
                  >
                    <LogOut size={13} aria-hidden="true" />
                    <span>Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Fix G1+G30: Breadcrumbs are now derived from the NAV route
         * hierarchy (Section → Item) instead of the navigation history
         * trail. The trail-based approach would push "Overview" off the
         * crumb whenever the user opened more than 3 pages in sequence,
         * surfacing arbitrary previously-visited pages instead of the
         * current page's actual home in the IA. The new logic: for the
         * current path, locate (a) which section it belongs to, and
         * (b) which nav item matches; render `Section / Item`. Detail
         * routes like `/agents/:id` get a trailing dynamic crumb derived
         * from the page-supplied currentLabel from NavigationContext. */}
        {(() => {
          // Flatten every nav item and remember its parent section.
          const FLAT = [];
          NAV.forEach(section => {
            (section.items || []).forEach(it => FLAT.push({ ...it, section: section.label }));
            (section.subGroups || []).forEach(g => (g.items || []).forEach(it => FLAT.push({ ...it, section: section.label, subgroup: g.label })));
          });
          const PRETTY = Object.fromEntries(FLAT.map(it => [it.path?.replace(/^\//, ''), it.label]));
          const pretty = (label) => PRETTY[label] || (label || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          // Find the nav item whose path is a prefix of the current pathname.
          // Longest-match wins so /agents/:id resolves to /agents, not /.
          const path = location.pathname;
          const matched = FLAT
            .filter(it => path === it.path || path.startsWith(it.path + '/'))
            .sort((a, b) => b.path.length - a.path.length)[0];
          const segments = [];
          if (matched) {
            segments.push({ label: matched.section, href: null });
            /* Fix G49: when the matched item lives under a subgroup
             * (Discovery > Agents, Discovery > Workflows, Governed
             * Gateway > API Keys, etc.), include the subgroup as a
             * non-clickable middle crumb so the user can locate the
             * page in the IA. Without this, /workflow-discovery and
             * /shadow-discovery both rendered as "Operate / Workflows"
             * and "Operate / Agents" — indistinguishable from
             * /canvas and /agents. */
            if (matched.subgroup) {
              segments.push({ label: matched.subgroup, href: null });
            }
            segments.push({ label: matched.label, href: matched.path });
          }
          // For detail routes, NavigationContext provides a currentLabel
          // (e.g., the agent name for /agents/:id) — append it as the
          // final, bold leaf.
          const leaf = currentLabel && (!matched || pretty(currentLabel) !== matched.label) ? pretty(currentLabel) : null;
          if (segments.length === 0 && !leaf) return null;
          return (
            <div className="bg-v2-surface border-b border-v2-border-subtle px-6 py-1.5 text-[11.5px] text-v2-ink-muted flex items-center gap-1.5">
              {segments.map((s, i) => (
                <React.Fragment key={i}>
                  {s.href ? (
                    <button
                      type="button"
                      onClick={() => navigate(s.href)}
                      className="v2-focus-ring hover:text-v2-ink transition-colors duration-v2"
                    >{s.label}</button>
                  ) : (
                    <span>{s.label}</span>
                  )}
                  {(i < segments.length - 1 || leaf) && <span aria-hidden="true">/</span>}
                </React.Fragment>
              ))}
              {leaf && <span className="text-v2-ink font-medium">{leaf}</span>}
            </div>
          );
        })()}

        {/* RC-56 — "← Back to {previous screen}" pill.
         *
         * Operator feedback: IA breadcrumbs answer "where am I in the
         * hierarchy?" and the recent-trail answers "what have I been
         * working on?" — but neither answers "where did I JUST come
         * from to get to this exact screen?". The browser back button
         * works for SPA routes, but operators don't always realise it
         * does, and they can't SEE the destination before clicking.
         *
         * Resolves the label via NavigationContext.previousScreen,
         * which prefers a recentEntities name (concrete entity), then
         * falls back to a route label (list pages), then humanises
         * the slug. Self-hides on the first navigation of a session
         * (no previous screen yet). */}
        {previousScreen && (
          <div className="bg-v2-surface border-b border-v2-border-subtle px-6 py-1 text-[11px] flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(previousScreen.url)}
              title={`Return to ${previousScreen.label}`}
              className="v2-focus-ring inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-v2-action-border bg-v2-action-soft/30 hover:bg-v2-action-soft hover:border-v2-action transition-colors duration-v2 text-v2-action-strong font-medium flex-shrink-0 max-w-[320px]"
            >
              <span aria-hidden="true">←</span>
              <span className="text-[9px] uppercase tracking-wide text-v2-ink-muted flex-shrink-0">
                {previousScreen.kind || 'Page'}
              </span>
              <span className="truncate">Back to {previousScreen.label}</span>
            </button>
          </div>
        )}

        {/* Recent-entities trail — the precise last-7 Agents / Workflows /
         * Skills / Connectors the user worked on, so they can jump straight
         * back. Independent of the IA breadcrumb above (which only shows the
         * current page's home in the navigation hierarchy). Powered by
         * NavigationContext.recordEntity, capped at RECENT_CAP. */}
        {recentEntities && recentEntities.length > 0 && (
          <div className="bg-v2-surface border-b border-v2-border-subtle px-6 py-1 text-[11px] flex items-center gap-2 overflow-x-auto">
            <span className="text-v2-ink-muted font-semibold flex-shrink-0">Recent</span>
            {recentEntities.map((e) => (
              <button
                key={e.path}
                type="button"
                onClick={() => navigate(e.path)}
                title={`${e.type}: ${e.name}`}
                className="v2-focus-ring flex items-center gap-1 px-2 py-0.5 rounded-full border border-v2-border-subtle hover:border-v2-action hover:bg-v2-action-soft/40 transition-colors duration-v2 flex-shrink-0 max-w-[200px]"
              >
                <span className="text-[9px] uppercase tracking-wide text-v2-ink-muted flex-shrink-0">{e.type}</span>
                <span className="text-v2-ink truncate">{e.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Page slot */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </main>

      {/*
       * RC-224 — Global toast stack. Portals into document.body so it
       * escapes any parent overflow:hidden (canvas pages need this).
       * Holds the queue managed by pushGlobalToast / dismissGlobalToast.
       * Mounted exactly ONCE — useFormTaskToastNotifier already pushes
       * onto this queue from the same app-shell render.
       */}
      <GlobalToastStack />
    </div>
  );
}
